import { icon as vaultIcon } from '../../ui/vault/icons.js';
import {
  appLogoSvg,
  bellIcon,
  chevronIcon,
  filterIcon,
  lockIcon,
  navIcon,
  plusIcon,
  searchIcon,
  syncDotIcon,
  syncIcon,
} from "../icons.js";
import { NAV_ITEMS } from "../constants.js";
import { escapeAttr, escapeHtml } from "../utils/html.js";
import { t } from "../../../../shared/i18n/index.js";

/**
 * Build the shell markup.
 *
 * Returns the assembled string plus the pieces it was assembled from, so the
 * caller can tell *which* region changed. The top bar carries the sync status,
 * which ticks on its own schedule as sync events arrive; without this split a
 * status dot changing colour rewrote the entire shell, and on a large document
 * that is a megabyte of outline re-parsed for a 7px circle.
 *
 * @returns {{html: string, shellClass: string, topbar: string, rest: string, activeHtml: string, navigation: string, overlays: string}}
 */
export function renderMobileShell(appState, shellState, deps) {
  const {
    activeHtml,
    currentUser,
    docTitle,
    filterCount,
    modeLabel,
    noAnim,
    overlayHtml,
    readOnly,
    serverHint,
    serverLevel,
    syncActive,
  } = shellState;

  const shellClass = `mobile-shell ${noAnim ? "no-sheet-anim" : ""}`;

  const topbar = `
      <header class="mobile-topbar">
        <!-- The logo TOGGLES the vault: opens it from any tab, and from the
             vault returns to the tab you were on (back to the current map). -->
        <button class="mobile-app-logo mobile-vault-trigger" data-mobile-action="toggle-vault" aria-label="${escapeAttr(t("html.vault.title"))}" aria-expanded="${deps.uiState.tab === 'docs'}">${vaultIcon('vault')}<span>${escapeHtml(t("html.vault.title"))}</span></button>
        <!-- Top bar targets match their scope: the logo (left) opens the vault,
             the document name opens that document's properties, the avatar
             (right) opens the account. Previously all of this was reachable
             only through a "More" tab that mixed the three together.
             The whole block is the button, not just the text: the name alone is
             a ~20px-tall target, whereas name + status line fills the bar. -->
        <button class="mobile-title-block" data-mobile-action="open-doc-props"
                title="${escapeAttr(docTitle)}"
                aria-label="${escapeAttr(t("mobile.shell.docProps", { title: docTitle }))}">
          <span class="mobile-title">
            <span class="mobile-title-text">${escapeHtml(docTitle)}</span>${chevronIcon()}
          </span>
          <span class="mobile-subtitle ${readOnly ? "locked" : ""} ${syncActive ? "syncing" : ""}"
                role="status" aria-live="polite" title="${escapeAttr(serverHint || "")}">${readOnly ? lockIcon() : syncDotIcon(serverLevel)}${escapeHtml(modeLabel)}</span>
        </button>
        ${currentUser
          ? `<button class="mobile-user-chip" data-mobile-action="open-user-info" aria-label="${escapeAttr(t("html.profile.account"))}" title="${escapeAttr(currentUser.username || currentUser.email || t("mobile.shell.user"))}">${escapeHtml((currentUser.username || currentUser.email || "U").slice(0, 1).toUpperCase())}</button>`
          : `<button class="mobile-login-btn" data-mobile-action="login" aria-label="${escapeAttr(t("html.header.loginToSync"))}">${syncIcon()}<span>${escapeHtml(t("html.header.login"))}</span></button>`}
        <div class="mobile-topbar-actions">
          ${(() => {
            const cbs = deps.callbacks;
            const count = (cbs?.getInvitations?.().length || 0)
              + (cbs?.getPendingNotifications?.().length || 0);
            return `
              <button class="mobile-icon-btn ${count ? "active" : ""}" data-mobile-action="open-notifications"
                      aria-label="${escapeAttr(count ? t("mobile.shell.notificationsCount", { n: count }) : t("html.header.notifications"))}">
                ${bellIcon()}${count ? `<span class="mobile-badge">${count > 9 ? "9+" : count}</span>` : ""}
              </button>`;
          })()}
          <button class="mobile-icon-btn" data-mobile-action="open-search" aria-label="${escapeAttr(t("common.search"))}">${searchIcon()}</button>
          <button class="mobile-icon-btn ${filterCount ? "active" : ""}" data-mobile-action="open-filter" aria-label="${escapeAttr(t("mobile.shell.filters"))}">
            ${filterIcon()}${filterCount ? `<span class="mobile-badge">${filterCount}</span>` : ""}
          </button>
        </div>
      </header>`;

  const navigation = renderBottomNav(deps.uiState);
  const overlays = `${renderFab(appState, deps)}${overlayHtml}`;
  const rest = `
      <main class="mobile-main">
        ${activeHtml}
      </main>
      ${navigation}
      <div class="mobile-overlays" style="display:contents">${overlays}</div>`;

  return {
    html: `
    <div class="${shellClass}">${topbar}${rest}
    </div>
  `,
    shellClass,
    topbar,
    rest,
    activeHtml,
    navigation,
    overlays,
  };
}

export function renderMobileError(err) {
  return `
    <section class="mobile-view mobile-view-padded">
      <div class="mobile-empty">
        ${escapeHtml(t("mobile.shell.renderError"))}
        ${err?.message ? `<br><span class="mobile-muted">${escapeHtml(err.message)}</span>` : ""}
      </div>
    </section>
  `;
}

function renderBottomNav(uiState) {
  return `
    <nav class="mobile-bottom-nav">
      ${NAV_ITEMS.map(([tab, labelKey]) => `
        <button class="mobile-bottom-nav-item ${uiState.tab === tab ? "active" : ""}"
                data-mobile-action="nav"
                data-tab="${tab}">
          <span class="mobile-bottom-nav-icon">${navIcon(tab)}</span>
          <span class="mobile-bottom-nav-label">${escapeHtml(t(labelKey))}</span>
        </button>
      `).join("")}
    </nav>
  `;
}

function renderFab(appState, { callbacks, uiState }) {
  if (callbacks.Store.isReadOnly?.()) return "";
  if (!["outline", "notes", "board", "mindmap", "bubble"].includes(uiState.tab)) return "";
  if (uiState.activeNodeId || uiState.form || uiState.confirm || uiState.searchOpen || uiState.filterOpen) return "";

  const config = {
    outline: ["add-node-to-selected", t("mobile.fab.addNode")],
    notes: ["new-note", t("mobile.fab.newNote")],
    board: ["add-node-to-selected", t("mobile.fab.addCard")],
    // Map tabs: same add flow — child of the selected node, or of the
    // focused/root node when nothing is selected.
    mindmap: ["add-node-to-selected", t("mobile.fab.addNode")],
    bubble: ["add-node-to-selected", t("mobile.fab.addNode")],
  }[uiState.tab];
  if (!config) return "";
  const [action, label] = config;
  if (!appState.data && action !== "new-note") return "";

  return `
    <button class="mobile-fab"
            data-mobile-action="${action}"
            data-mobile-longpress-action="${uiState.tab === "outline" ? "add-node-to-root" : ""}"
            aria-label="${escapeAttr(label)}"
            title="${escapeAttr(t("mobile.fab.title", { label }))}">${plusIcon()}</button>
  `;
}
